import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TablePagination,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Send,
  Info,
  Receipt,
  Person,
  Home,
  AttachMoney,
  CheckCircle,
  Warning,
  Business,
  TrendingDown,
  Percent
} from '@mui/icons-material';

const RateConcession = () => {
  const canWriteConcession = hasWritePermission('plot-booking.rate-concession');
  const [searchForm, setSearchForm] = useState({
    bookingId: '',
  });

  const [plotRateInput, setPlotRateInput] = useState('');
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestForm, setRequestForm] = useState({
    currentRate: '',
    requestedRate: '',
    concessionPercentage: '',
    reason: '',
    justification: '',
  });
  const [searchLoading, setSearchLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  // Sample booking data for rate concession
  const [bookingData] = useState([
    {
      id: 1,
      bookingId: 'BK001',
      customerId: 'CUST001',
      customerName: 'John Smith',
      siteName: 'Green Valley',
      sectorName: 'Sector A',
      blockName: 'Block 1',
      plotNo: 'P101',
      rate: '₹4,500/sq ft',
      plotAmount: '₹54,00,000',
      plotSize: '30x40 ft',
      plotArea: '1200 sq ft',
      bookingDate: '2025-01-15',
      paymentPlan: 'Installment',
      paidAmount: '₹5,40,000',
      balanceAmount: '₹48,60,000',
      mobileNo: '9876543210',
      status: 'Active',
    },
    {
      id: 2,
      bookingId: 'BK002',
      customerId: 'CUST002',
      customerName: 'Sarah Johnson',
      siteName: 'Hill View',
      sectorName: 'Sector B',
      blockName: 'Block 2',
      plotNo: 'P205',
      rate: '₹5,200/sq ft',
      plotAmount: '₹81,90,000',
      plotSize: '35x45 ft',
      plotArea: '1575 sq ft',
      bookingDate: '2025-02-20',
      paymentPlan: 'Down Payment',
      paidAmount: '₹20,47,500',
      balanceAmount: '₹61,42,500',
      mobileNo: '9876543211',
      status: 'Active',
    },
    {
      id: 3,
      bookingId: 'BK003',
      customerId: 'CUST003',
      customerName: 'Michael Brown',
      siteName: 'Paradise Heights',
      sectorName: 'Sector C',
      blockName: 'Block 3',
      plotNo: 'P321',
      rate: '₹6,000/sq ft',
      plotAmount: '₹1,20,00,000',
      plotSize: '40x50 ft',
      plotArea: '2000 sq ft',
      bookingDate: '2025-03-10',
      paymentPlan: 'Installment',
      paidAmount: '₹12,00,000',
      balanceAmount: '₹1,08,00,000',
      mobileNo: '9876543212',
      status: 'Active',
    },
    {
      id: 4,
      bookingId: 'BK004',
      customerId: 'CUST004',
      customerName: 'Emily Davis',
      siteName: 'Royal Gardens',
      sectorName: 'Sector D',
      blockName: 'Block 4',
      plotNo: 'P456',
      rate: '₹7,500/sq ft',
      plotAmount: '₹1,85,62,500',
      plotSize: '45x55 ft',
      plotArea: '2475 sq ft',
      bookingDate: '2025-04-05',
      paymentPlan: 'Full Payment',
      paidAmount: '₹37,12,500',
      balanceAmount: '₹1,48,50,000',
      mobileNo: '9876543213',
      status: 'Active',
    },
  ]);

  const concessionReasons = [
    'Market Rate Adjustment',
    'Bulk Booking Discount',
    'Early Bird Offer',
    'Customer Loyalty Discount',
    'Competitive Pricing',
    'Special Circumstances',
    'Promotional Offer',
    'Other',
  ];

  const handleInputChange = (field, value) => {
    setSearchForm({
      ...searchForm,
      [field]: value,
    });
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: ''
      });
    }
  };

  const handleSearch = () => {
    if (!searchForm.bookingId.trim()) {
      setErrors({ bookingId: 'Booking ID is required' });
      return;
    }

    setErrors({});
    setSearchLoading(true);

    // Simulate API call
    setTimeout(() => {
      setShowBookingDetails(true);
      setPage(0);
      setTableSearchQuery('');
      setSuccessMessage('');
      setSearchLoading(false);
      console.log('Searching for booking:', searchForm.bookingId);
    }, 1000);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSendRateRequest = (booking) => {
    setSelectedBooking(booking);
    setRequestForm({
      currentRate: booking.rate,
      requestedRate: '',
      concessionPercentage: '',
      reason: '',
      justification: '',
    });
    setRequestDialogOpen(true);
  };

  const handleRequestFormChange = (field, value) => {
    const newForm = { ...requestForm, [field]: value };

    // Auto-calculate concession percentage if rates are provided
    if (field === 'requestedRate' || field === 'currentRate') {
      const currentRateNum = parseFloat(newForm.currentRate.replace(/[^0-9.]/g, ''));
      const requestedRateNum = parseFloat(newForm.requestedRate.replace(/[^0-9.]/g, ''));

      if (currentRateNum && requestedRateNum) {
        const percentage = ((currentRateNum - requestedRateNum) / currentRateNum * 100).toFixed(2);
        newForm.concessionPercentage = percentage > 0 ? `${percentage}%` : '0%';
      }
    }

    setRequestForm(newForm);
  };

  const handleSubmitRequest = () => {
    if (requestForm.requestedRate && requestForm.reason && selectedBooking) {
      setSuccessMessage(`Rate concession request submitted successfully for Booking ${selectedBooking.bookingId}`);
      setRequestDialogOpen(false);
      setRequestForm({
        currentRate: '',
        requestedRate: '',
        concessionPercentage: '',
        reason: '',
        justification: '',
      });
      setSelectedBooking(null);
      console.log('Rate concession request submitted:', requestForm);
    }
  };

  const getFilteredBookingData = () => {
    if (!showBookingDetails || !searchForm.bookingId.trim()) return [];

    let filteredData = bookingData.filter(booking =>
      booking.bookingId.toLowerCase().includes(searchForm.bookingId.toLowerCase()) &&
      booking.status === 'Active'
    );

    // Filter by table search query
    if (tableSearchQuery.trim()) {
      filteredData = filteredData.filter(booking =>
        Object.values(booking).some(value =>
          String(value).toLowerCase().includes(tableSearchQuery.toLowerCase())
        )
      );
    }

    return filteredData;
  };

  const calculateNewPlotAmount = (currentAmount, percentage) => {
    const amount = parseFloat(currentAmount.replace(/[^0-9.]/g, ''));
    const concessionPercent = parseFloat(percentage.replace(/[^0-9.]/g, ''));
    const newAmount = amount * (1 - concessionPercent / 100);
    return `₹${newAmount.toLocaleString('en-IN')}`;
  };

  const getSearchSummary = () => {
    const filteredData = getFilteredBookingData();
    return `${filteredData.length} booking(s) found for "${searchForm.bookingId}"`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <TrendingDown sx={{ color: '#1976d2', fontSize: 32 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
          Rate Concession Management
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert
          severity="success"
          sx={{
            mb: 4,
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle sx={{ fontSize: 20 }} />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Receipt sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Bookings
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  4
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#fff3e0', 
            border: '1px solid #ffcc02',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Warning sx={{ color: '#f57c00', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Pending Requests
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  2
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <CheckCircle sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Approved Requests
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  1
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#ffebee', 
            border: '1px solid #ffcdd2',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Business sx={{ color: '#d32f2f', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Rejected Requests
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  1
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#f3e5f5', 
            border: '1px solid #ce93d8',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Percent sx={{ color: '#7b1fa2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Avg. Concession
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  15%
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid> */}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <TrendingDown sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Booking for Rate Concession
            </Typography>
          </Box>

          <Box display="flex" gap={2} alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Booking ID"
                variant="outlined"
                fullWidth
                value={searchForm.bookingId}
                onChange={(e) => handleInputChange('bookingId', e.target.value)}
                placeholder="Enter Booking ID (e.g., BK001)"
                error={!!errors.bookingId}
                helperText={errors.bookingId || "Enter the booking ID to search for rate concession eligibility"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', height: '56px', mt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={searchLoading || !searchForm.bookingId.trim()}
                startIcon={<Search />}
                sx={{
                  height: '40px',
                  minHeight: '40px',
                  minWidth: '160px',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2
                }}
              >
                {searchLoading ? 'Searching...' : 'Search Booking'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Booking Details Table */}
      {showBookingDetails && (
        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <Box display="flex" alignItems="center" gap={2}>
                <Receipt sx={{ color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                  Booking Details for Rate Concession
                </Typography>
              </Box>
              <TextField
                label="Search Details..."
                variant="outlined"
                size="small"
                value={tableSearchQuery}
                onChange={(e) => setTableSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
            </Box>

            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Info sx={{ fontSize: 20 }} />
                {getSearchSummary()}
              </Box>
            </Alert>

            {getFilteredBookingData().length > 0 ? (
              <>
                <TableContainer sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>S.No</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Customer Details</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Booking Info</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Plot Location</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Plot Details</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>Rate & Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredBookingData()
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((booking, index) => (
                          <TableRow key={booking.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                            <TableCell sx={{ fontWeight: 600 }}>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Person sx={{ color: '#666', fontSize: 16 }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                    {booking.customerName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {booking.customerId}
                                  </Typography>
                                  <br />
                                  <Typography variant="caption" color="text.secondary">
                                    📱 {booking.mobileNo}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Receipt sx={{ color: '#666', fontSize: 16 }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    {booking.bookingId}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    📅 {booking.bookingDate}
                                  </Typography>
                                  <br />
                                  <Chip
                                    label={booking.status}
                                    sx={{
                                      backgroundColor: '#4caf50',
                                      color: 'white',
                                      fontSize: '0.7rem',
                                      height: 20,
                                      fontWeight: 600
                                    }}
                                    size="small"
                                  />
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Home sx={{ color: '#666', fontSize: 16 }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                    {booking.siteName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    📍 {booking.sectorName}
                                  </Typography>
                                  <br />
                                  <Typography variant="caption" color="text.secondary">
                                    🏢 {booking.blockName}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Home sx={{ color: '#666', fontSize: 16 }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                    Plot #{booking.plotNo}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    📏 {booking.plotSize}
                                  </Typography>
                                  <br />
                                  <Typography variant="caption" color="text.secondary">
                                    📐 {booking.plotArea}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <AttachMoney sx={{ color: '#666', fontSize: 16 }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#f57c00' }}>
                                    Rate: {booking.rate}
                                  </Typography>
                                  <Divider sx={{ my: 0.5 }} />
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                                    Amount: {booking.plotAmount}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <TablePagination
                    
                    component="div"
                    count={getFilteredBookingData().length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>

                {/* Plot Rate Request Section */}
                <Card sx={{ mt: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                      <TrendingDown sx={{ color: '#1976d2', fontSize: 28 }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2' }}>
                        Plot Rate Concession Request
                      </Typography>
                    </Box>

                    <Box display="flex" gap={2} alignItems="flex-start">
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          label="Plot Rate"
                          variant="outlined"
                          fullWidth
                          placeholder="Please enter plot rate"
                          value={plotRateInput}
                          onChange={(e) => setPlotRateInput(e.target.value)}
                          helperText="Enter the new requested rate per sq ft (e.g., ₹4000/sq ft)"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoney color="action" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', height: '56px', mt: 1 }}>
                        {/* {canWriteConcession && ( */}
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            const firstBooking = getFilteredBookingData()[0];
                            if (firstBooking) {
                              setRequestForm({
                                ...requestForm,
                                currentRate: firstBooking.rate,
                                requestedRate: plotRateInput,
                              });
                              handleSendRateRequest(firstBooking);
                            }
                          }}
                          startIcon={<Send />}
                          disabled={!plotRateInput || getFilteredBookingData().length === 0}
                          sx={{
                            height: '40px',
                            minHeight: '40px',
                            minWidth: '160px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '15px',
                            boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                            borderRadius: 2
                          }}
                        >
                          Send Request
                        </Button>
                        {/* )} */}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Alert severity="warning" sx={{ mt: 3, borderRadius: 2 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Warning sx={{ fontSize: 20 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      No Booking Found
                    </Typography>
                    <Typography variant="body2">
                      No active booking found with Booking ID: "{searchForm.bookingId}"
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Please check the booking ID and try again.
                    </Typography>
                  </Box>
                </Box>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Rate Concession Request Dialog */}
      <Dialog open={requestDialogOpen} onClose={() => setRequestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ color: 'primary.main' }}>
            Submit Rate Concession Request
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box sx={{ mt: 2 }}>
              {/* Booking Summary */}
              <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: '#f9f9f9' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'info.main' }}>
                  <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Booking Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Booking ID:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedBooking.bookingId}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Customer:</Typography>
                    <Typography variant="body1">{selectedBooking.customerName}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Plot No:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      {selectedBooking.plotNo}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Current Rate:</Typography>
                    <Typography variant="body1" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                      {selectedBooking.rate}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Current Amount:</Typography>
                    <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                      {selectedBooking.plotAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">Plot Area:</Typography>
                    <Typography variant="body1">{selectedBooking.plotArea}</Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Request Form */}
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'error.main' }}>
                  Rate Concession Request Details
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <TextField
                      label="Current Rate"
                      variant="outlined"
                      fullWidth
                      value={requestForm.currentRate}
                      disabled
                      helperText="Current rate per sq ft"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Requested Rate *"
                      variant="outlined"
                      fullWidth
                      value={requestForm.requestedRate}
                      onChange={(e) => handleRequestFormChange('requestedRate', e.target.value)}
                      placeholder="₹0/sq ft"
                      helperText="Enter the new requested rate per sq ft"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Concession Percentage"
                      variant="outlined"
                      fullWidth
                      value={requestForm.concessionPercentage}
                      disabled
                      helperText="Auto-calculated based on rates"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Concession Reason *</InputLabel>
                      <Select
                        value={requestForm.reason}
                        label="Concession Reason *"
                        onChange={(e) => handleRequestFormChange('reason', e.target.value)}
                      >
                        {concessionReasons.map((reason) => (
                          <MenuItem key={reason} value={reason}>
                            {reason}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Justification Details"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={4}
                      value={requestForm.justification}
                      onChange={(e) => handleRequestFormChange('justification', e.target.value)}
                      placeholder="Provide detailed justification for the rate concession request..."
                      helperText="Explain the reasons and circumstances for requesting rate concession"
                    />
                  </Grid>

                  {requestForm.requestedRate && requestForm.concessionPercentage && (
                    <Grid item xs={12}>
                      <Paper elevation={1} sx={{ p: 2, backgroundColor: '#e8f5e8' }}>
                        <Typography variant="body2" color="text.secondary">New Plot Amount (After Concession):</Typography>
                        <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                          {calculateNewPlotAmount(selectedBooking.plotAmount, requestForm.concessionPercentage)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Savings: {requestForm.concessionPercentage} discount applied
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          {/* {canWriteConcession && ( */}
          <Button
            onClick={handleSubmitRequest}
            color="primary"
            variant="contained"
            disabled={!requestForm.requestedRate || !requestForm.reason}
            startIcon={<Send />}
          >
            Submit Request
          </Button>
          {/* )} */}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RateConcession;
